// openExtensionPreview — make every AI app build VISIBLE the moment it lands
// (2026-07-24 per Nika "when it builds app it should also always open preview").
//
// The client half of render_extension_page / create_app used to render ONLY
// into the /add-app canvas (window.__addAppHost); a build kicked off from any
// other surface (the home composer, the docked drawer on a page) queued
// silently with a "open the Add App surface" note. This registers the built
// app as a TRANSIENT preview page and opens it in the shell PageSlot beside
// the conversation — the same split sheet answer views use — so the user
// always SEES what was built, wherever they asked for it.
//
// The def renders through ExtensionEmbed (components/ExtensionEmbed.js): the
// same guarded iframe pipeline as the /add-app pane (audit → guarded parse →
// entity-scoped bridge → isolated realm). Nothing about the preview weakens
// the extension isolation model — the PageSlot page IS the iframe host.
//
// Route shape: '/x-preview/<slug>-<seq>' — seq keeps every render unique so
// the PageSlot keep-alive (same-path remounts are skipped) can never pin a
// stale build when the same app is iterated. Stored in root._aiAnswerPages —
// the same session-transient map answer views live in (PageSlot._defFor
// already resolves any route from it, and its pruning keeps the map bounded).
// metadata.__extensionApp carries { projectId, name } so the PageSlot header
// can offer "Add to Workspace" on preview routes, and the thread's view block
// its "Save as app" (functions/assistantThreadBlocks.js). `open: false` only
// registers the view (the main thread shows it inline).

const MAX_ANSWER_PAGES = 8

let _previewSeq = 0

const _shellSlug = (title) =>
  String(title || 'app')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 48) || 'app'

export function openExtensionPreview(payload = {}) {
  const el = this && this.getRoot ? this : arguments[0]
  const args = this && this.getRoot ? payload : arguments[1] || {}
  const name = String(args.name || 'App preview').trim() || 'App preview'
  const pages = Array.isArray(args.pages) ? args.pages : []
  const source = typeof args.source === 'string' ? args.source : ''
  if (!source.trim() && !pages.length) {
    return { ok: false, error: 'source or pages is required' }
  }

  const root = el.getRootState?.() || el.state?.root || el.getRoot?.()?.state
  if (!root || typeof root.update !== 'function') {
    return { ok: false, error: 'no root state — cannot open the app preview' }
  }

  const projectId = args.projectId ? String(args.projectId) : null
  const route = `/x-preview/${_shellSlug(name)}-${++_previewSeq}`
  // IN-SHELL mount (2026-07-25 per Nika "it does not have to be in iframe"):
  // the preview def IS the audited/parsed/scoped app def, so it renders as a
  // native workspace view — live design system, house + module components.
  const built = pages.length
    ? el.call('buildInlineMultiPageDef', { name, pages, entities: args.entities || [] })
    : el.call('buildInlineExtensionDef', {
        name,
        source,
        entities: Array.isArray(args.entities) ? args.entities : []
      })
  // A contained refusal (generatedAppContainment.js) still opens: the preview
  // shows the blocked-feature state, never any of the app's own code.
  if (!built || (built.ok === false && !(built.blocked && built.def))) {
    return { ok: false, error: (built && built.error) || 'build failed' }
  }
  const def = {
    ...built.def,
    metadata: { ...(built.def.metadata || {}), title: name, __extensionApp: { projectId, name } }
  }

  const entries = Object.entries(root._aiAnswerPages || {}).slice(-(MAX_ANSWER_PAGES - 1))
  entries.push([route, def])
  // `open: false` — registered only, the split sheet stays shut: the MAIN
  // THREAD shows the preview inline as a view block (AI_ASSISTANT_SPEC §12.1
  // Apps, functions/simoneAiTransport.js).
  const open = args.open !== false
  root.update(
    open
      ? { _aiAnswerPages: Object.fromEntries(entries), _canvasSlot: route, _slotFullscreen: null }
      : { _aiAnswerPages: Object.fromEntries(entries) },
    { preventFetch: true }
  )
  return { ok: true, opened: route, title: name, shown: open }
}
